import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Trophy, Radio } from 'lucide-react';
import { API } from '../config';

const playfairFont = "'Playfair Display', serif";

const TIER_COLORS = {
  gold: '#D4AF37',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
  common: '#A3B8AC',
  last: '#EF4444',
};

const TIER_LABEL = {
  gold: 'GOLD', silver: 'SILVER', bronze: 'BRONZE',
  common: 'COMMON', last: 'LAST PRIZE',
};

const FALLBACK_PRIZE =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23202824" width="100" height="100"/><text x="50" y="50" fill="%23A3B8AC" font-family="sans-serif" font-size="10" text-anchor="middle" dy=".3em">?</text></svg>';

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * Realtime Winner Feed — polls /api/kuji/feed every 5s.
 * - Pure read of /api/kuji/feed (no fake data — backend pulls from kuji_history only)
 * - Variants: 'compact' (homepage / list), 'inline' (event detail, with optional eventId filter)
 * - Auto-pauses polling when tab is hidden (battery + bandwidth friendly)
 */
export const WinnerFeed = ({ variant = 'compact', eventId = null, limit = 20, className = '' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(typeof document === 'undefined' ? true : !document.hidden);
  const timerRef = useRef(null);

  const fetchFeed = async () => {
    try {
      const { data } = await axios.get(`${API}/kuji/feed`, { params: { limit } });
      let list = data || [];
      if (eventId) list = list.filter((d) => d.event_id === eventId);
      setItems(list);
    } catch (_e) {
      // silently swallow; surface as empty state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onVis = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (!visible) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }
    fetchFeed();
    timerRef.current = setInterval(fetchFeed, 5000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, eventId, limit]);

  if (loading && items.length === 0) return null;
  if (!loading && items.length === 0) return null; // hide gracefully if no draws yet

  return (
    <section
      className={`rounded-2xl backdrop-blur-xl overflow-hidden ${className}`}
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
      data-testid="winner-feed"
    >
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-2">
          <motion.div
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Radio className="w-4 h-4" style={{ color: '#EF4444' }} />
          </motion.div>
          <h3 className="text-sm font-medium tracking-wider uppercase" style={{ color: '#D4AF37' }}>
            Live Winners {eventId ? '(this event)' : ''}
          </h3>
        </div>
        <span className="text-[10px]" style={{ color: '#A3B8AC' }}>{items.length} recent</span>
      </div>

      <div className={variant === 'inline' ? 'max-h-80 overflow-y-auto' : 'max-h-96 overflow-y-auto'}>
        <AnimatePresence initial={false}>
          {items.map((it) => (
            <WinnerRow key={it.id} item={it} />
          ))}
        </AnimatePresence>
      </div>
    </section>
  );
};

const WinnerRow = ({ item }) => {
  const tierColor = TIER_COLORS[item.prize_tier_snapshot] || '#F8FAF9';
  const isLast = !!item.is_last_prize;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="flex items-center gap-3 px-4 py-2.5"
      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
      data-testid={`winner-row-${item.id}`}
    >
      <img
        src={item.prize_image_snapshot || FALLBACK_PRIZE}
        alt={item.prize_name_snapshot}
        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
        onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_PRIZE; }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-medium truncate" style={{ color: '#F8FAF9', fontFamily: playfairFont }}>
            {item.username_masked}
          </span>
          <span className="text-xs" style={{ color: '#A3B8AC' }}>won</span>
          <span className="text-sm font-medium truncate" style={{ color: tierColor }}>
            {item.prize_name_snapshot}
          </span>
          {isLast && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(239,68,68,0.2)', color: '#EF4444' }}>
              ★ LAST
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <Trophy className="w-3 h-3" style={{ color: tierColor }} />
          <span className="text-[10px] uppercase tracking-wider" style={{ color: tierColor }}>
            {TIER_LABEL[item.prize_tier_snapshot] || item.prize_tier_snapshot}
          </span>
          <span className="text-[10px] truncate" style={{ color: '#A3B8AC' }}>· {item.event_name_snapshot}</span>
        </div>
      </div>
      <span className="text-[10px] flex-shrink-0" style={{ color: '#A3B8AC' }}>{timeAgo(item.created_at)}</span>
    </motion.div>
  );
};
